import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { clearAccessTokens } from '@ecp/common/src/utils/utils';
import useLoginForward from '@ecp/common/src/hooks/useLoginForward';
import moTheme from '@ecp/common/src/style/theme/mo';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import { TextButton } from '@ecp/common/src/components/button/mo/TextButton';
import { ReactComponent as InquiryIcon } from '@ecp/common/src/assets/icon/mo_skypass_icon_inquiry.svg';
import { ReactComponent as FaqIcon } from '@ecp/common/src/assets/icon/mo_skypass_icon_faq.svg';

import { getIsLogin } from '@mo-recoil/common/user/selector';
import { getOtoQuestUseYn } from '@mo-recoil/system/selector';
import useSiteInfo from '@mo-hooks/common/useSiteInfo';
import { useConfirmDialog } from '@mo-hooks/common/useModal';

const ContactInfoDesc = () => {
  const { systemInformation } = useSiteInfo();

  const { opTime, lunchTime, customerCenterGuide } = useMemo(
    () => ({
      opTime: `평일 ${systemInformation.customerCenterOpStartHh} - ${systemInformation.customerCenterOpEndHh}`,
      lunchTime: `점심시간 ${systemInformation.customerCenterLchStartHh} - ${systemInformation.customerCenterLchEndHh}`,
      customerCenterGuide: systemInformation.customerCenterGuide,
    }),
    [systemInformation]
  );

  return (
    <FlexBoxColumn align-items={'flex-start'} width={'100%'}>
      <FlexBox>
        <TextBox
          size={moTheme.font.size.$13}
          weight={moTheme.font.weight.demiLight}
          height={moTheme.font.lineHeight.demiLight}
        >
          고객지원
        </TextBox>
        <Spacing left={'4px'} />
        <TextBox
          size={moTheme.font.size.$13}
          weight={moTheme.font.weight.medium}
          height={moTheme.font.lineHeight.demiLight}
        >
          {systemInformation.customerCenterTel}
        </TextBox>
      </FlexBox>
      <Spacing top={'3px'} />
      <TextBox
        size={moTheme.font.size.$12}
        weight={moTheme.font.weight.demiLight}
        height={moTheme.font.lineHeight.demiLight}
      >
        {`${opTime} ${lunchTime} (${customerCenterGuide})`}
      </TextBox>
    </FlexBoxColumn>
  );
};

const ActionIcons = () => {
  const isLogin = useRecoilValue(getIsLogin);
  const otoQuestUseYn = useRecoilValue(getOtoQuestUseYn);
  const { showConfirmDialog } = useConfirmDialog();
  const { siteNumber } = useSiteInfo();
  const loginForward = useLoginForward(siteNumber, showConfirmDialog);
  const navigate = useNavigate();

  const handleLoginClick = async () => {
    clearAccessTokens();
    if (!isLogin) {
      loginForward();
    }
  };

  const handleOnClickMoveToFaq = () => {
    navigate('/customer-service/faq');
  };

  const handleOnClickMoveTo1on1 = () => {
    navigate('/customer-service/qna');
  };

  return (
    <FlexBox width={'100%'}>
      {otoQuestUseYn && isLogin && (
        <TextButton
          type="custom"
          textProps={{ weight: 700, fontSize: '13px'}}
          onClick={handleOnClickMoveTo1on1}
          customIcon={InquiryIcon}
          isReverse
        >
          1:1문의
        </TextButton>
      )}
      {otoQuestUseYn && !isLogin && (
        <TextButton
          type="custom"
          textProps={{ weight: 700, fontSize: '13px' }}
          onClick={handleLoginClick}
          customIcon={InquiryIcon}
          isReverse
        >
          1:1문의
        </TextButton>
      )}
      <Spacing right={'24px'} />
      <TextButton
        type="custom"
        textProps={{ weight: 700, fontSize: '13px' }}
        onClick={handleOnClickMoveToFaq}
        customIcon={FaqIcon}
        isReverse
      >
        FAQ
      </TextButton>
    </FlexBox>
  );
};

const ContactInfo = () => {
  return (
    <>
      <ContactInfoContainer>
        <ContactInfoDesc />
        <Spacing top={10} />
        <ActionIcons />
      </ContactInfoContainer>
    </>
  );
};

export default ContactInfo;

const ContactInfoContainer = styled(FlexBoxColumn)`
  background-color: ${(props) => props.theme.color.background.basic};
  padding: 14px 20px;
  width: 100%;
`;
